import React, { useCallback, useEffect, useRef, useState } from 'react';

import { GEETEST_ID } from '../../helpers/settings';

export interface GeeTestResult {
  lot_number: string;
  captcha_output: string;
  pass_token: string;
  gen_time: string;
}

interface GeeTestCaptchaProps {
  onVerify?: (result: GeeTestResult) => void;
  onError?: (error: any) => void;
  autoLoad?: boolean;
}

declare global {
  interface Window {
    initGeetest4?: any;
  }
}

// Хук для работы с GeeTest
export const useGeeTest = (onSuccess?: (data: GeeTestResult) => void) => {
  const [loaded, setLoaded] = useState(false);
  const instanceRef = useRef<any>(null);

  useEffect(() => {
    let attempts = 0;
    const checkGeetest = () => {
      attempts++;

      if (window.initGeetest4) {
        setLoaded(true);
      } else {
        if (attempts > 50) {
          // 5 секунд максимум
          console.error('[GeeTest] Не удалось загрузить скрипт GeeTest');
          return;
        }
        setTimeout(checkGeetest, 100);
      }
    };
    checkGeetest();
  }, []);

  const showCaptcha = useCallback(() => {
    if (!GEETEST_ID) {
      console.error('[GeeTest] GEETEST_ID не настроен');
      return Promise.reject({ message: 'GEETEST_ID not configured' });
    }

    if (!loaded) {
      console.error('[GeeTest] Скрипт GeeTest еще не загружен');
      return Promise.reject({ message: 'GeeTest not loaded yet' });
    }

    return new Promise<GeeTestResult>((resolve, reject) => {
      try {
        const config = {
          captchaId: GEETEST_ID,
          product: 'popup',
          protocol: 'https://',
          timeout: 10000,
          language: 'rus',
        };

        window.initGeetest4?.(config, (captchaObj: any) => {
          captchaObj.onSuccess(() => {
            const result = captchaObj.getValidate();

            if (result) {
              const geetestData = {
                lot_number: result.lot_number,
                captcha_output: result.captcha_output,
                pass_token: result.pass_token,
                gen_time: result.gen_time,
              };
              resolve(geetestData);
              onSuccess?.(geetestData);
            } else {
              reject({ message: 'Failed to get validate from GeeTest' });
            }
          });

          captchaObj.onError((err: any) => {
            console.error('[GeeTest] Ошибка капчи:', err);
            reject(err);
          });

          captchaObj.onClose(() => {
            reject({ message: 'Captcha closed by user' });
          });

          captchaObj.onReady(() => {
            instanceRef.current = captchaObj;

            // Показываем капчу сразу после инициализации
            try {
              captchaObj.showBox();

              // Принудительно показываем капчу (фикс для popup режима)
              setTimeout(() => {
                const boxWrap = document.querySelector(
                  '[class*="geetest_box_wrap"]',
                ) as HTMLElement;
                const box = document.querySelector(
                  '[class*="geetest_box"]:not([class*="wrap"]):not([class*="logo"])',
                ) as HTMLElement;
                const popupWrap = document.querySelector(
                  '[class*="geetest_popup_wrap"]',
                ) as HTMLElement;

                // Применяем стили для видимости
                if (boxWrap) {
                  boxWrap.style.setProperty('display', 'block', 'important');
                  boxWrap.style.setProperty('visibility', 'visible', 'important');
                  boxWrap.style.setProperty('opacity', '1', 'important');
                  boxWrap.style.setProperty('z-index', '999999', 'important');
                  boxWrap.style.setProperty('position', 'fixed', 'important');
                  boxWrap.style.setProperty('top', '50%', 'important');
                  boxWrap.style.setProperty('left', '50%', 'important');
                  boxWrap.style.setProperty(
                    'transform',
                    'translate(-50%, -50%)',
                    'important',
                  );
                }

                if (box) {
                  box.style.setProperty('display', 'block', 'important');
                  box.style.setProperty('visibility', 'visible', 'important');
                  box.style.setProperty('opacity', '1', 'important');
                  box.style.setProperty('z-index', '999999', 'important');
                }

                if (popupWrap) {
                  popupWrap.style.setProperty('z-index', '999999', 'important');
                  popupWrap.style.setProperty('position', 'fixed', 'important');
                }
              }, 100);
            } catch (error) {
              console.error('[GeeTest] Ошибка при показе капчи:', error);
            }
          });
        });
      } catch (error) {
        console.error('[GeeTest] Ошибка инициализации:', error);
        reject(error);
      }
    });
  }, [loaded, onSuccess]);

  return { showCaptcha, loaded };
};

// Компонент для интеграции GeeTest (скрытый)
export const GeeTestCaptcha: React.FC<GeeTestCaptchaProps> = ({
  onVerify,
  onError,
  autoLoad = false,
}) => {
  const { showCaptcha, loaded } = useGeeTest(onVerify);

  useEffect(() => {
    if (autoLoad && loaded) {
      showCaptcha().catch((err) => {
        onError?.(err);
      });
    }
  }, [autoLoad, loaded, showCaptcha, onError]);

  return null;
};
